import type { CaptureResult } from "posthog-js";
import { sanitizePostHogProperties } from "./posthog-privacy.ts";

const WEB_VITALS_SAMPLE_RATE = 0.1;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isOpaqueScriptError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions)) {
    const values = event.properties.$exception_values;
    const sources = event.properties.$exception_sources;
    return Array.isArray(values) && values.length === 1 && values[0] === "Script error." &&
      (sources == null || (Array.isArray(sources) && sources.length === 0));
  }
  if (exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "Error" || exception.value !== "Script error.") return false;
  const sources = event.properties.$exception_sources;
  if (sources != null && (!Array.isArray(sources) || sources.length > 0)) return false;
  if (exception.stacktrace == null) return true;
  return isRecord(exception.stacktrace) && Array.isArray(exception.stacktrace.frames) &&
    exception.stacktrace.frames.length === 0;
}

function isNativeMediaControlsError(event: CaptureResult): boolean {
  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return false;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "TypeError" || exception.value !== "Type error") return false;
  const stacktrace = exception.stacktrace;
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames) || stacktrace.frames.length !== 1) return false;
  const frame: unknown = stacktrace.frames[0];
  if (!isRecord(frame) || frame.function !== "sortedTrackListForMenu") return false;
  return frame.filename == null || frame.filename === "" ||
    frame.filename === "[native code]" || frame.filename === "Unknown Source";
}

function shouldKeepWebVitals(sessionId: unknown): boolean {
  if (typeof sessionId !== "string" || !sessionId) return true;
  let hash = 2166136261;
  for (let index = 0; index < sessionId.length; index++) {
    hash = Math.imul(hash ^ sessionId.charCodeAt(index), 16777619);
  }
  return (hash >>> 0) % 100 < WEB_VITALS_SAMPLE_RATE * 100;
}

export function beforeSendToPostHog(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  if (event.event === "$exception" && (isOpaqueScriptError(event) || isNativeMediaControlsError(event))) return null;
  if (event.event === "$web_vitals" && !shouldKeepWebVitals(event.properties.$session_id)) return null;
  const properties = sanitizePostHogProperties(event.properties, true);
  const environment = process.env.NEXT_PUBLIC_VERCEL_ENV;
  const hasMetadata = Boolean(environment);
  const result: CaptureResult = {
    ...event,
    properties: {
      ...properties,
      ...(hasMetadata ? {
        platform: "web",
        deployment_environment: environment,
        git_commit: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
        telemetry_schema_version: 2,
      } : {}),
      ...(event.event === "$web_vitals" ? {
        web_vitals_sample_rate: typeof event.properties.$session_id === "string" && event.properties.$session_id
          ? WEB_VITALS_SAMPLE_RATE : 1,
      } : {}),
    },
  };
  for (const key of ["$set", "$set_once"] as const) {
    const value = event[key];
    if (isRecord(value)) result[key] = sanitizePostHogProperties(value);
  }
  const hasTopLevelChanges = result.$set !== event.$set || result.$set_once !== event.$set_once;
  return properties === event.properties && !hasMetadata && event.event !== "$web_vitals" &&
    !hasTopLevelChanges ? event : result;
}
